import { datadogRum } from '@datadog/browser-rum'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useIsAuthenticated } from '@azure/msal-react'

import { useMockEnvironment } from '@contexts/mockEnv'
import { api } from '@services/apiClient'
import { queryClient } from '@services/queryClient'

import { mockedMe } from './mocks/me'

interface Filter {
  statusIds?: number[]
  areaIds?: number[]
  locationIds?: number[]
  supervisorIds?: number[]
  resourceIds?: number[]
  plantSectionIds?: number[]
  operationPostIds?: number[]
  assigneeIds?: number[]
  projectIds?: number[]
  supervisorRole: 0 | 1
}

type Role =
  | 'global-admin'
  | 'mechanical'
  | 'technician'
  | 'flsupervisor'
  | 'process'
  | 'scheduler'
  | 'admin'
  | 'read'

interface Site {
  code: string
  country: string
  customTileId: string
  dateFormat: number
  filters: Filter
  id: number
  name: string
  region: string
  roles: Role[]
  shiftEndTime: number
  shiftStartTime: number
  startOfWeek: number
  timeFormat: number
  timeZoneName: string
  utcOffset: number
}

interface User {
  email: string
  displayName: string
  isGlobalAdmin: boolean
  landId: string
  selectedSite: Site
  sites: Site[]
}

export function useFindProfile() {
  const { isMockEnvironment } = useMockEnvironment()
  const isAuthenticated = useIsAuthenticated()
  const ONE_HOUR = 1000 * 60 * 60

  async function fetchProfile() {
    if (!isMockEnvironment) {
      const { data } = await api.get<User>('/me')

      handleDataDogUserInfo(data)

      return data
    }
    return mockedMe
  }

  function handleDataDogUserInfo(data: User) {
    datadogRum.setUser({
      id: data.landId,
      name: data.displayName,
      email: data.email,
    })

    datadogRum.setGlobalContextProperty('selectedSiteInfo', data.selectedSite)
  }

  return useQuery({
    queryKey: ['PROFILE'],
    queryFn: fetchProfile,
    staleTime: ONE_HOUR,
    enabled: isAuthenticated || isMockEnvironment,
  })
}

export function useEditProfile() {
  async function putProfile(siteId: number) {
    const { data } = await api.put(`me/selectedSite/${siteId}`)

    return data
  }

  return useMutation({
    mutationFn: putProfile,
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: ['PROFILE'],
        type: 'active',
      })
    },
  })
}
